/**
 * Permission strings the client gates navigation on.
 *
 * The server is the authority: these only decide what is *shown*. Project
 * permissions mirror `korosbuild/core/permissions.py` (`Perm`) and arrive in
 * `/me` `projects[].permissions` already resolved (role bundle ∪ grants −
 * revokes, client-side wall applied). Check permissions, never role names.
 */
import type { Me } from '@/lib/api/types';

export const PERMISSIONS = {
  // Project scope — mirrors koros-build-api `core.permissions.Perm`.
  projectView: 'project.view',
  projectEdit: 'project.edit',
  /** Complete or reopen a project (owner side). */
  projectArchive: 'project.archive',
  membersView: 'members.view',
  membersManage: 'members.manage',
  partiesManage: 'parties.manage',
  siteView: 'site.view',
  diaryWrite: 'diary.write',
  diaryApprove: 'diary.approve',
  progressWrite: 'progress.write',
  progressApprove: 'progress.approve',
  issuesWrite: 'issues.write',
  issuesClose: 'issues.close',
  planningManage: 'planning.manage',
  tasksUpdate: 'tasks.update',
  mediaUpload: 'media.upload',
  issuesViewAll: 'issues.view_all',
  /** Every photo/document, not only those shared with the client side. */
  mediaViewAll: 'media.view_all',
  reportsView: 'reports.view',
  workforceView: 'workforce.view',
  workforceManage: 'workforce.manage',
  /** Worker phone numbers and payout details (contractor side only). */
  workforceViewPersonal: 'workforce.view_personal',
  attendanceCapture: 'attendance.capture',
  attendanceApprove: 'attendance.approve',
  costView: 'cost.view',
  /** Wages, rates and margins (contractor side only). */
  costViewInternal: 'cost.view_internal',
  payrollPrepare: 'payroll.prepare',
  /** Contractor-side review of a payroll run (not by its preparer). */
  payrollApprove: 'payroll.approve',
  /** The payer's sign-off and payment; step-up required. */
  paymentAuthorize: 'payment.authorize',
  /**
   * The owner side's read-only labour evidence (owner, owner's PM, viewer, QS):
   * the project's own payroll runs and lines and the daily timesheets — never
   * a phone, ID or payout number.
   */
  labourEvidenceView: 'labour_evidence.view',
  /** Prepare and edit bills of quantities (online). */
  boqEdit: 'boq.edit',
  /** Owner side: agree a BOQ version as the contract baseline; step-up required. */
  boqApprove: 'boq.approve',
  variationsWrite: 'variations.write',
  /** Owner side: decide variations; approving needs step-up. */
  variationsApprove: 'variations.approve',
  /** Record expenses (offline-capable). */
  expensesWrite: 'expenses.write',
  /** Contractor side: approve, reject and mark paid (not the recorder). */
  expensesApprove: 'expenses.approve',
  /** Draft and submit requisitions (offline-capable). */
  procurementRequest: 'procurement.request',
  /** RFQs, purchase orders, buyer-side invoice capture. */
  procurementManage: 'procurement.manage',
  /** Approve requisitions, orders and invoices (never one's own). */
  procurementApprove: 'procurement.approve',
  /** Receive goods on site (offline-capable). */
  deliveriesReceive: 'deliveries.receive',
  documentsView: 'documents.view',
  documentsManage: 'documents.manage',
  /** Quality records involving one's side (the owner side: shared ones). */
  qualityView: 'quality.view',
  /** Contractor side: checklists, inspection requests, NCR/snag management, tests. */
  qualityManage: 'quality.manage',
  /** Perform inspections (offline), raise NCRs, sign off someone else's inspection. */
  qualityInspect: 'quality.inspect',
  /** Submit rectifications for NCRs and snags assigned to one's company (offline). */
  qualityRectify: 'quality.rectify',
  /** Raise snags (the owner side too, offline) and verify the snags one's side raised. */
  qualitySnag: 'quality.snag',
  /** The safety summary: counts and trends, never names. */
  safetyView: 'safety.view',
  /** Report incidents, record toolbox talks and attendance (offline; contractor side). */
  safetyReport: 'safety.report',
  /** Investigate and close incidents, safety walks, the PPE log. */
  safetyManage: 'safety.manage',
  /** Raise constraints blocking work (offline; the contractor side, a subcontractor its own). */
  constraintsRaise: 'constraints.raise',
  /** Draft the daily plan (offline) and close the day (online). */
  sitePlan: 'site.plan',
  /** Crews and their foremen (the contractor's; a subcontractor its own). */
  crewsManage: 'crews.manage',
  /** Site meetings, minutes and the action register. */
  meetingsManage: 'meetings.manage',
  escalationsRaise: 'escalations.raise',
  /** The gate's visitor log (offline; contractor side). */
  visitorsManage: 'visitors.manage',
  /** The consultant / owner side issues site instructions. */
  instructionsIssue: 'instructions.issue',
  /** The contractor acknowledges site instructions (online). */
  instructionsAcknowledge: 'instructions.acknowledge',
  /** The site store: stock on hand, movements, counts (contractor side). */
  inventoryView: 'inventory.view',
  /** Issue materials to crews and take returns (offline). */
  inventoryIssue: 'inventory.issue',
  /** Store items, transfers, wastage, counts, fulfilling requests (online). */
  inventoryManage: 'inventory.manage',
  /** Ask the store for materials (offline); see one's party's issues. */
  materialsRequest: 'materials.request',
  /** The contractor submits materials for approval. */
  materialsSubmit: 'materials.submit',
  /** The consultant / owner side reviews material submittals. */
  materialsApproveSubmittal: 'materials.approve_submittal',
  /** Materials on site at PO price, for valuations (contractor and its QS; never the owner side). */
  materialsOnSite: 'materials.on_site',
  /** The plant register (a subcontractor: its own). */
  equipmentView: 'equipment.view',
  /** Register, hire, repairs, allocating requests (online). */
  equipmentManage: 'equipment.manage',
  /** Daily logs and pre-use checks (offline). */
  equipmentOperate: 'equipment.operate',
  /** Ask for plant for dates and a task. */
  equipmentRequest: 'equipment.request',
  /** Module 6: issued drawings, transmittals, specification, deliverables (the owner side too). */
  designView: 'design.view',
  /** Issue drawings and revisions, transmittals, specification, deliverables (online). */
  designIssue: 'design.issue',
  /** Raise RFIs and technical queries (offline; the builder's side). */
  rfiRaise: 'rfi.raise',
  /** Answer RFIs assigned to one's company (online; never the raiser). */
  rfiRespond: 'rfi.respond',
  /** Put design submittals forward (shop drawings, method statements, approvals, O&M, as-builts). */
  submittalsSubmit: 'submittals.submit',
  /** Review design submittals with an A–D code (the consultant / owner side). */
  submittalsReview: 'submittals.review',
  /** Record site visits and observations (offline; the consultant / owner's PM). */
  sitevisitsRecord: 'sitevisits.record',
  /** The contractor asks for practical completion. */
  completionRequest: 'completion.request',
  /** Certify practical completion (the consultant / owner side; online). */
  completionCertify: 'completion.certify',
  /** Module 7: the main contract's commercials — both sides of the contract, never a subcontractor. */
  commercialView: 'commercial.view',
  /** Contract terms, provisional sums, adjustments, assessments, the final account (the employer's side). */
  commercialManage: 'commercial.manage',
  /** Prepare valuations and certificates (online). */
  valuationsPrepare: 'valuations.prepare',
  /** Measurement-sheet rows on site (offline). */
  measurementsRecord: 'measurements.record',
  /** The contractor agrees or disputes a certificate, an assessment, the final account. */
  certificatesReview: 'certificates.review',
  /** The certifier named in the contract certifies payment (online). */
  certificatesCertify: 'certificates.certify',
  /** The employer records the payments it made. */
  paymentsRecord: 'payments.record',
  /** Submit (contractor) or assess (employer's side) claims; disputes. */
  claimsManage: 'claims.manage',
  /** Give and answer contractual notices. */
  noticesIssue: 'notices.issue',
  /** Elemental cost plans (one's own side's). */
  costplanManage: 'costplan.manage',
  /** Module 8: the contractor's subcontracts, or a subcontractor's own (never the owner side). */
  subcontractsView: 'subcontracts.view',
  /** Packages, invitations, agreements, checking mobilisation, acknowledging reports (the contractor). */
  subcontractsManage: 'subcontracts.manage',
  /** Approve an award (never the package's preparer or recommender). */
  subcontractsAward: 'subcontracts.award',
  /** Certify a subcontractor's application (the contractor's PM or QS; online). */
  subcontractsCertify: 'subcontracts.certify',
  /** Record paying a subcontract certificate (the contractor; online). */
  subcontractsPay: 'subcontracts.pay',
  /** The subcontractor accepts its agreement, agrees or disputes certificates and back-charges. */
  subcontractsAccept: 'subcontracts.accept',
  /** The subcontractor's payment applications, invoices and variation requests. */
  subcontractsApply: 'subcontracts.apply',
  /** The subcontractor's daily reports (offline). */
  subReportsSubmit: 'sub_reports.submit',
  /** Read them: the contractor's site team (never subcontract prices), the subcontractor its own. */
  subReportsView: 'sub_reports.view',
  /** The contractor proposes back-charges and deductions. */
  backchargesManage: 'backcharges.manage',
  /** The employer consents to a nominated subcontractor (no prices). */
  subcontractsConsent: 'subcontracts.consent',
  /** Module 9: change requests and selections (the main contract's two sides; never a subcontractor). */
  changesView: 'changes.view',
  /** Raise a change request (the employer's side). */
  changesRequest: 'changes.request',
  /** Price a change request: cost and time (the contractor). */
  changesPrice: 'changes.price',
  /** Accept (→ an instructed variation) or reject a priced change request (the owner side; online). */
  changesDecide: 'changes.decide',
  /** Prepare finish and fixture options against the allowance (the contractor / consultant). */
  selectionsPrepare: 'selections.prepare',
  /** Choose a selection option (the owner side; online). */
  selectionsDecide: 'selections.decide',
  /** The risk register (shared risks; one's own side's internal ones). */
  risksView: 'risks.view',
  risksManage: 'risks.manage',
  /** Warranties and warranty claims (a subcontractor: the claims assigned to it). */
  warrantyView: 'warranty.view',
  /** Keep the warranty register and assign claims (the contractor). */
  warrantyManage: 'warranty.manage',
  /** Raise warranty claims (offline) and close them (the owner side). */
  warrantyClaim: 'warranty.claim',
  /** Answer a warranty claim assigned to one's company (online). */
  warrantyRespond: 'warranty.respond',
  /** The employer tenders the main contract (the owner, owner's PM, the employer's QS). */
  tendersManage: 'tenders.manage',
  /** Approve the main contract's award (the owner; never its preparer or recommender). */
  tendersAward: 'tenders.award',
  /** Rate the companies on the project (the owner side). */
  ratingsGive: 'ratings.give',
  // Module 11: messaging. Who takes part in a conversation is decided by the
  // server from sides, parties and the record's own rules; these gate screens.
  messagesView: 'messages.view',
  /** Write in the conversations one takes part in (offline-capable). */
  messagesSend: 'messages.send',
  /** Post announcements to one's side or to the workers. */
  messagesBroadcast: 'messages.broadcast',
  /** Pin, and remove others' messages, where one takes part. */
  messagesModerate: 'messages.moderate',
  // Prototype gaps (party-scoped rights).
  /** A subcontractor's PPE register for the workers its own party engaged. */
  ppeManageOwn: 'ppe.manage_own',
  /** Raise coordination (clash) items between disciplines (offline, like RFIs). */
  coordinationRaise: 'coordination.raise',
  /** A subcontractor's own purchases and stock, private to its party. */
  purchasesOwn: 'purchases.own',
  /**
   * A subcontractor's own workers (party-scoped): read its own roster, crews and
   * attendance; engage, edit and end them, keep their rates and payout numbers.
   */
  workforceViewOwn: 'workforce.view_own',
  workforceManageOwn: 'workforce.manage_own',
  /** A subcontractor paying its own workers: approve (step-up) and pay its own payroll. */
  payrollAuthorizeOwn: 'payroll.authorize_own',
  /** A subcontractor lead brings its own colleagues onto its own side. */
  membersInviteOwn: 'members.invite_own',
  /**
   * The marketplace's hiring side (owner, owner's PM, contractor admin, project
   * manager, subcontractor lead): openings, quote requests, service jobs and
   * invitations to join — private to the party.
   */
  servicesHire: 'services.hire',

  // Organisation scope (organizations[].permissions) — core.permissions.OrgPerm.
  orgView: 'org.view',
  orgMembersView: 'org.members.view',
  /** Invite people, change roles, remove members (owner, admin). */
  orgMembersManage: 'org.members.manage',
  orgSettingsManage: 'org.settings.manage',
  projectsCreate: 'projects.create',
  // Supplier organisations (Phase 4).
  catalogManage: 'catalog.manage',
  quotesSubmit: 'quotes.submit',
  ordersManage: 'orders.manage',
  invoicesManage: 'invoices.manage',
  paymentsView: 'payments.view',
  // Module 12: branch stock and lead times (warehouse, dispatch); the company's
  // own sales read models (customers, analytics).
  stockManage: 'stock.manage',
  salesView: 'sales.view',
  // Land seller organisations (Phase 6): listings, uploads, enquiries.
  landManage: 'land.manage',
  // Subcontractor companies (module 8): price the packages they are invited to.
  tendersSubmit: 'tenders.submit',
  // Module 10: the company's public profile and portfolio, its shortlist, a
  // professional firm's fee tracker (company-internal) and job postings.
  orgProfileManage: 'org.profile.manage',
  shortlistManage: 'shortlist.manage',
  feesManage: 'fees.manage',
  jobsManage: 'jobs.manage',
  // Module 13: the company's templates and the accounting export of its own books.
  templatesManage: 'templates.manage',
  accountingExport: 'accounting.export',

  // Person scope (me.permissions) — core.permissions.PlatformPerm.
  admin: 'build.admin',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export function projectPermissions(me: Me | null, projectId: string | undefined): Set<string> {
  const project = me?.projects.find((entry) => entry.id === projectId);
  return new Set(project?.permissions ?? []);
}

export function orgPermissions(me: Me | null, orgId: string | undefined): Set<string> {
  const org = me?.organizations.find((entry) => entry.id === orgId);
  return new Set(org?.permissions ?? []);
}

/** Person-level rights plus every organisation's (for "anywhere" checks like admin or create project). */
export function anyOrgPermissions(me: Me | null): Set<string> {
  return new Set([
    ...(me?.permissions ?? []),
    ...(me?.organizations.flatMap((org) => org.permissions) ?? []),
  ]);
}

/** Supplier organisations the person acts for (a person may also be a buyer). */
export function supplierOrgs(me: Me | null) {
  return me?.organizations.filter((org) => org.kind === 'supplier') ?? [];
}

/** Land seller organisations the person acts for. */
export function landSellerOrgs(me: Me | null) {
  return me?.organizations.filter((org) => org.kind === 'land_seller') ?? [];
}

/** Organisations in which the person may create projects. */
export function orgsWithPermission(me: Me | null, permission: Permission) {
  return me?.organizations.filter((org) => org.permissions.includes(permission)) ?? [];
}
